import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import type {
  Appearance,
  StripeCardNumberElementOptions,
  StripeElementLocale,
  StripeElementsOptions as StripeElementsOptionsType,
} from '@stripe/stripe-js';
import { intl } from '@frameio/i18n';
import { useMediaQuery } from '@frameio/utils/hooks';
import { dangerouslyReadGlobalThemeValues, useThemeName } from '@frameio/vapor';

// values that are not available in theme
const valueFromTheme = {
  // the Stripe Appearance API variables don't support rgba, so we need to use rgb
  colorsTextPrimaryWithoutAlpha: 'rgb(255, 255, 255)',
  colorsTextPrimaryWithoutAlphaLightTheme: 'rgb(24,27,41)',
  colorsBrandPrimary: 'rgba(91, 83, 255, 1)',
  // solid rgb mirrors of the brand/background palette tokens for use as
  // Appearance API variables (which reject rgba)
  colorsBrandPrimaryWithoutAlpha: 'rgb(91, 83, 255)',
  colorsBackgroundPrimaryWithoutAlpha: 'rgb(31, 35, 50)', // palette.grey[12]
  colorsBackgroundPrimaryWithoutAlphaLightTheme: 'rgb(255, 255, 255)', // palette.grey[100]
  inputFontSize: '13px',
  inputFontSizeMobile: '16px',
};

const mobileBreakpoint = 'screen and (pointer: coarse) and (max-width: 768px)';

type StripeElementsOptions = {
  appearance: Appearance;
  locale?: StripeElementLocale;
};

const { fonts, palette, radii, spaces, transitions } =
  dangerouslyReadGlobalThemeValues;

// This object styles the AddressElement within the iframe
// the styles are applied within an iframe, so we can't use our theme tokens
// https://stripe.com/docs/elements/appearance-api
const stripeElementsOptions: StripeElementsOptions = {
  appearance: {
    theme: 'stripe',
    // variables are applied globally within the Stripe Element.
    // Stripe derives surface, border, tab and Link colors from colorBackground,
    // so it must be set to a dark value for the PaymentElement tabs/blocks/Link
    // UI to render in the dark theme.
    variables: {
      colorBackground: valueFromTheme.colorsBackgroundPrimaryWithoutAlpha,
      colorPrimary: valueFromTheme.colorsBrandPrimaryWithoutAlpha,
      colorText: valueFromTheme.colorsTextPrimaryWithoutAlpha,
      borderRadius: radii[4],
      fontFamily: fonts.body,
      fontSizeBase: valueFromTheme.inputFontSize,
      spacingGridRow: spaces[16],
      spacingGridColumn: spaces[16],
    },
    // rules are applied to specific elements within the Stripe Element
    rules: {
      '.Input': {
        backgroundColor: palette.greyAlpha[10],
        border: 'none',
        boxShadow: `inset 0 0 0 1.5px ${palette.greyAlpha[15]}`,
        outline: 'none',
        transition: `box-shadow 0.25s ${transitions.eases.primary},color 0.25s ${transitions.eases.primary}`,
      },

      '.Input:hover': {
        boxShadow: `inset 0 0 0 1.5px ${palette.greyAlpha[40]}`,
        border: 'none',
      },

      '.Input:focus': {
        boxShadow: `inset 0 0 0 1px ${valueFromTheme.colorsBrandPrimary},0 0 0 1px ${valueFromTheme.colorsBrandPrimary}`,
        border: 'none',
      },

      '.Input::placeholder': {
        color: palette.greyAlpha[50],
      },

      '.Input--invalid': {
        color: palette.grey[100],
      },

      '.Label': {
        color: palette.greyAlpha[60],
        fontFamily: fonts.body,
        fontSize: valueFromTheme.inputFontSize,
        marginBottom: spaces[6],
      },
    },
  },
};

const stripeElementsOptionsLightTheme: StripeElementsOptions = {
  ...stripeElementsOptions,
  appearance: {
    ...stripeElementsOptions.appearance,
    variables: {
      ...stripeElementsOptions.appearance.variables,
      colorBackground:
        valueFromTheme.colorsBackgroundPrimaryWithoutAlphaLightTheme,
      colorText: valueFromTheme.colorsTextPrimaryWithoutAlphaLightTheme,
    },
    rules: {
      ...stripeElementsOptions.appearance.rules,
      '.Input': {
        ...stripeElementsOptions.appearance.rules?.['.Input'],
        backgroundColor: palette.grey[93],
        boxShadow: `inset 0 0 0 1.5px ${palette.greyAlpha[15]}`,
        color: palette.grey[10],
      },

      '.Input:hover': {
        ...stripeElementsOptions.appearance.rules?.['.Input:hover'],
        boxShadow: `inset 0 0 0 1.5px ${palette.greyAlpha[40]}`,
      },

      '.Input::placeholder': {
        ...stripeElementsOptions.appearance.rules?.['.Input::placeholder'],
        color: palette.grey[60],
      },

      '.Input--invalid': {
        color: palette.grey[10],
      },

      '.Label': {
        ...stripeElementsOptions.appearance.rules?.['.Label'],
        color: palette.grey[40],
      },
    },
  },
};

type StripeCardElementVars = CSSProperties & Record<`--${string}`, string>;

const stripeCardElementVars: StripeCardElementVars = {
  '--stripe-element-bg': palette.greyAlpha[10],
  '--stripe-element-font-size': valueFromTheme.inputFontSize,
  '--stripe-element-shadow-default': `inset 0 0 0 1.5px ${palette.greyAlpha[15]}`,
  '--stripe-element-shadow-hover': `inset 0 0 0 1.5px ${palette.greyAlpha[40]}`,
};

const stripeCardElementVarsLightTheme: StripeCardElementVars = {
  ...stripeCardElementVars,
  '--stripe-element-bg': palette.grey[93],
};

export function useStripeCardElementStyle(): StripeCardElementVars {
  const themeName = useThemeName();

  return themeName === 'light'
    ? stripeCardElementVarsLightTheme
    : stripeCardElementVars;
}

// these styles are applied within the iframe, so we can't use theme tokens
const cardElementOptions: StripeCardNumberElementOptions = {
  style: {
    base: {
      color: palette.grey[100],
      fontFamily: fonts.body,
      fontSize: valueFromTheme.inputFontSize,
      '::placeholder': {
        color: palette.greyAlpha[50],
      },
    },
  },
  showIcon: true,
};

const cardElementOptionsLightTheme: StripeCardNumberElementOptions = {
  ...cardElementOptions,
  style: {
    ...cardElementOptions.style,
    base: {
      ...cardElementOptions.style?.base,
      color: palette.grey[10],
      '::placeholder': {
        color: palette.grey[60],
      },
    },
    invalid: {
      color: palette.tint.red[50],
    },
  },
};

export function useStripeElementsOptions(): StripeElementsOptionsType {
  const themeName = useThemeName();
  const isMobile = useMediaQuery(mobileBreakpoint);
  const localeContext = intl();

  return useMemo(() => {
    const options: StripeElementsOptions =
      themeName === 'light'
        ? stripeElementsOptionsLightTheme
        : stripeElementsOptions;

    // we can cast the type to StripeElementLocale because Stripe will
    // fallback to en-US if the locale is not supported
    const locale = localeContext.locale as StripeElementLocale;

    const baseOptions = {
      ...options,
      locale,
    };

    if (!isMobile) return baseOptions;

    return {
      ...baseOptions,
      appearance: {
        ...baseOptions.appearance,
        variables: {
          ...baseOptions.appearance.variables,
          fontSizeBase: valueFromTheme.inputFontSizeMobile,
        },
      },
    };
  }, [isMobile, themeName, localeContext.locale]);
}

export function useCardElementOptions(): StripeCardNumberElementOptions {
  const themeName = useThemeName();

  const isMobile = useMediaQuery(mobileBreakpoint);

  return useMemo(() => {
    const options: StripeCardNumberElementOptions =
      themeName === 'light' ? cardElementOptionsLightTheme : cardElementOptions;

    if (!isMobile) return options;

    return {
      ...options,
      style: {
        ...options.style,
        base: {
          ...options.style?.base,
          fontSize: valueFromTheme.inputFontSizeMobile,
        },
      },
    };
  }, [isMobile, themeName]);
}
